"use client";
import axios from "axios";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import Spinner from "@/ui/Spinner";
import { logEvent } from "@/ui/analytics/google";
import { Button } from "@/ui/atoms/Button";
import Notification from "@/ui/components/Notification";
import { formatCents } from "@/lib/utils";
import { collectionStatement, collectionSlug } from "@/lib/collections";
import { TIER_LABEL, TIER_ORDER, HIDDEN_SIZES } from "@/lib/variants";
import { type CatalogVariant, type ImageDetail } from "@/ui/types";

const API_URL = process.env.NEXT_PUBLIC_MICROGRAPHER_API_URL;
const API_KEY = process.env.NEXT_PUBLIC_MICROGRAPHER_API_KEY;

const CDN = "https://d127u13u6yy1k7.cloudfront.net";

// Tier order + labels come from @/lib/variants (shared with cart + confirmation).

// Flatten variants_by_tier into a single ordered list (excluding unfulfillable sizes; HIDDEN_SIZES
// + the fulfillment rationale live in @/lib/variants — TODO §E.6).
function flattenVariants(detail: ImageDetail): CatalogVariant[] {
	return TIER_ORDER.flatMap((tier) => detail.variants_by_tier[tier] ?? []).filter(
		(v) => !HIDDEN_SIZES.has(v.size),
	);
}

export default function Page(props: { params: { slug: string } }) {
	const slug = props.params.slug;
	const [detail, setDetail] = useState<ImageDetail | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isError, setIsError] = useState(false);
	const [selectedSku, setSelectedSku] = useState<string | null>(null);
	const [showNotification, setShowNotification] = useState(false);
	const [notificationMessage, setNotificationMessage] = useState("");
	const [notificationType, setNotificationType] = useState<"success" | "error">("success");

	useEffect(() => {
		axios
			.get<ImageDetail>(`${API_URL}/products/${slug}`, {
				headers: { "x-api-key": API_KEY },
			})
			.then((res) => setDetail(res.data))
			.catch((error) => {
				console.error("Error fetching product:", error);
				setIsError(true);
			})
			.finally(() => setIsLoading(false));
	}, [slug]);

	const variants = useMemo(() => (detail ? flattenVariants(detail) : []), [detail]);

	// Default selection: first buyable variant, else first available, else first.
	useEffect(() => {
		if (variants.length && selectedSku === null) {
			const buyable = variants.find((v) => v.available && !isSoldOut(v));
			const available = variants.find((v) => v.available);
			setSelectedSku((buyable ?? available ?? variants[0]).sku);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [variants, selectedSku]);

	const selected = variants.find((v) => v.sku === selectedSku) ?? null;

	const imgUrl = detail
		? `${CDN}/static/images/products/${detail.img_name}/${detail.img_name}-${detail.orientation}-no-frame-no-mat.webp`
		: "";

	// A variant is sold out if its edition pool has 0 remaining (limited only; open = null).
	const isSoldOut = (variant: CatalogVariant): boolean => {
		const edition = detail?.editions[variant.edition_ref];
		return edition?.remaining != null && edition.remaining <= 0;
	};
	// Purchasable = Stripe-backed AND not sold out.
	const canBuy = (variant: CatalogVariant): boolean => variant.available && !isSoldOut(variant);

	// Scarcity for the selected limited variant, if any.
	const selectedEdition = selected ? detail?.editions[selected.edition_ref] : undefined;
	const selectedSoldOut = selected ? isSoldOut(selected) : false;

	const handleSelect = (variant: CatalogVariant) => {
		setSelectedSku(variant.sku);
		logEvent("Click", "Update Product Variant", variant.sku);
	};

	const notify = (message: string, type: "success" | "error") => {
		setNotificationMessage(message);
		setNotificationType(type);
		setShowNotification(true);
		setTimeout(() => setShowNotification(false), 3000);
	};

	const handleAddToCart = async () => {
		if (!detail || !selected || !canBuy(selected)) return;
		try {
			logEvent("Button", "Click", "Add To Cart");
			const response = await axios.post(
				`${API_URL}/cart`,
				{
					sku: selected.sku,
					stripe_price_id: selected.stripe_price_id,
					img_name: detail.img_name,
					product_name: detail.title,
					tier: selected.tier,
					size: selected.size,
					edition_ref: selected.edition_ref,
					signed: selected.signed,
					price_cents: selected.price_cents,
					orientation: detail.orientation,
					quantity: 1,
				},
				{
					withCredentials: true,
					headers: { "x-api-key": API_KEY, "Content-Type": "application/json" },
				},
			);
			if (response.data?.status_code === 200) {
				notify("Item added to cart!", "success");
				window.dispatchEvent(new Event("cart:changed")); // refresh the nav badge
			} else {
				notify("Failed to add item to cart.", "error");
			}
		} catch (error) {
			console.error("Error adding to cart:", error);
			notify("Error adding to cart.", "error");
		}
	};

	if (isLoading) {
		return (
			<section className="mx-auto flex max-w-gallery justify-center px-5 py-section sm:px-8">
				<Spinner />
			</section>
		);
	}

	if (isError || !detail) {
		return (
			<section className="mx-auto max-w-gallery px-5 py-section sm:px-8">
				<p className="py-section-sm text-center text-ink-muted">
					We couldn&apos;t load this print. Please refresh or return to the{" "}
					<Link href="/shop" className="link-gallery">
						collection
					</Link>
					.
				</p>
			</section>
		);
	}

	const series = detail.series;
	const statement = collectionStatement(series);

	return (
		<section className="mx-auto max-w-gallery px-5 py-section-sm sm:px-8">
			<div className="grid gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-16">
				{/* Image — given dominance (wider column), framed by space, no border/shadow. */}
				<div className="relative aspect-square w-full overflow-hidden bg-bone-deep lg:sticky lg:top-28 lg:self-start">
					{/* ASSET: the artwork at high resolution (≥1600px square-ish), true colour, no crop. */}
					<Image
						src={imgUrl}
						alt={`${detail.title} — crystallized ${detail.substance}`}
						fill
						priority
						sizes="(min-width: 1024px) 52rem, 100vw"
						className="object-cover"
					/>
				</div>

				{/* Quiet, narrower info column. */}
				<div className="flex flex-col lg:pt-4">
					{/* Specimen label: series → title → substance/technique (the moat concentrates here). */}
					{series ? (
						<Link href="/shop" className="label-caps mb-5 text-ink-muted hover:text-mg-gold-deep">
							{series}
						</Link>
					) : null}
					<h1 className="font-serif text-title font-light leading-tight text-mg-black">{detail.title}</h1>
					{detail.substance ? (
						<p className="mt-3 text-ink-muted">
							Crystallized <span className="text-mg-black">{detail.substance}</span>, photographed under
							polarized light.
						</p>
					) : null}

					<p className="mt-6 font-serif text-3xl font-light text-mg-black" data-testid="ProductElement_Price">
						{selected?.price_cents != null ? formatCents(selected.price_cents) : "Coming soon"}
					</p>

					{/* The story of the piece. */}
					{detail.description ? (
						<p className="mt-8 max-w-prose leading-relaxed text-ink-muted">{detail.description}</p>
					) : null}

					{/* Provenance / specimen details for the selected variant. */}
					{selected ? (
						<dl className="mt-8 space-y-3 border-t border-mg-black/10 pt-8">
							<SpecRow label="Edition">{TIER_LABEL[selected.tier]}</SpecRow>
							{selectedEdition?.edition_size != null ? (
								<SpecRow label="Numbered">
									{selectedSoldOut
										? `Edition of ${selectedEdition.edition_size} — sold out`
										: `${selectedEdition.remaining ?? selectedEdition.edition_size} of ${
												selectedEdition.edition_size
										  } remaining`}
								</SpecRow>
							) : null}
							{selected.signed ? <SpecRow label="Signature">Signed &amp; numbered in pencil</SpecRow> : null}
							<SpecRow label="Provenance">Signed Certificate of Authenticity &amp; registry entry</SpecRow>
						</dl>
					) : null}

					{/* Variant selector — elegant acquisition control. NOTE (R.4.4 / §E.5): keep this
					    structure extensible so a future frame/mat/size configurator can slot in. */}
					<div className="mt-10">
						<p className="label-caps mb-4 text-ink-muted">Choose your print</p>
						<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
							{variants.map((variant) => {
								const isSelected = variant.sku === selectedSku;
								const soldOut = isSoldOut(variant);
								const selectable = variant.available && !soldOut;
								return (
									<button
										type="button"
										key={variant.sku}
										disabled={!selectable}
										onClick={() => handleSelect(variant)}
										className={`border px-3 py-3 text-left transition duration-300 ease-gallery ${
											isSelected
												? "border-mg-black bg-mg-black text-bone"
												: "border-mg-black/20 text-mg-black hover:border-mg-gold-deep"
										} ${selectable ? "cursor-pointer" : "cursor-not-allowed opacity-40"}`}
									>
										<span className={`label-caps block ${isSelected ? "text-bone/70" : "text-ink-faint"}`}>
											{TIER_LABEL[variant.tier]}
										</span>
										<span className="mt-1 block font-serif text-lg">{variant.size}</span>
										<span className={`mt-1 block text-sm ${isSelected ? "text-bone/80" : "text-ink-muted"}`}>
											{soldOut
												? "Sold out"
												: variant.price_cents != null
												? formatCents(variant.price_cents)
												: "—"}
										</span>
									</button>
								);
							})}
						</div>
					</div>

					<div className="mt-10">
						<Button
							onClick={handleAddToCart}
							disabled={!selected || !canBuy(selected)}
							className="w-full sm:w-auto"
						>
							{!selected?.available ? "Coming soon" : selectedSoldOut ? "Sold out" : "Add to Cart"}
						</Button>
					</div>

					{/* Collection cross-sell (R.4.5). */}
					{series && statement ? (
						<div className="mt-12 border-t border-mg-black/10 pt-8">
							<p className="label-caps mb-2 text-ink-muted">Part of {series}</p>
							<p className="max-w-prose text-sm leading-relaxed text-ink-muted">{statement}</p>
							{collectionSlug(series) ? (
								<Link
									href={`/collections/${collectionSlug(series)}`}
									className="link-gallery mt-3 inline-block text-sm"
								>
									View the collection
								</Link>
							) : null}
						</div>
					) : null}
				</div>
			</div>
			<Notification message={notificationMessage} show={showNotification} type={notificationType} />
		</section>
	);
}

// Specimen-label row: quiet dt/dd pair (small-caps label + value) — the archival treatment.
function SpecRow({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex gap-4">
			<dt className="label-caps w-28 shrink-0 pt-0.5 text-ink-faint">{label}</dt>
			<dd className="text-sm text-mg-black">{children}</dd>
		</div>
	);
}
